/**
 * Phase 2 opt-in per-asset action menu.
 *
 * Behavior contract — critical for TOCA non-regression:
 *   1. Calls GET /api/v2/assets/{id}/transitions.
 *   2. If the API returns 409 WORKFLOW_NOT_CONFIGURED or 404 (feature off /
 *      SGS), the hook returns null and this component renders nothing —
 *      the caller's existing legacy menu shows unchanged.
 *   3. Only when a binding exists does this component render the
 *      engine-driven menu. TOCA tenants without bindings see ZERO visual
 *      change per ADR-015.
 *
 * Caller pattern:
 *   <StageActionsDropdown assetId={asset.assetId}
 *                         fallback={<LegacyMenuItems />} />
 *
 * The fallback prop is rendered when the engine has nothing to say.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, GitBranch } from 'lucide-react';
import { useValidTransitions, type ValidTransition } from '@/api/hooks/useWorkflows';
import { TransitionDialog } from './TransitionDialog';

interface Props {
  assetId: string;
  /** Rendered when the engine has no binding for this asset. */
  fallback?: React.ReactNode;
  /** Optional className for the trigger button. */
  triggerClassName?: string;
  /** Optional callback fired after a successful transition. */
  onTransitionSuccess?: () => void;
}

export function StageActionsDropdown({
  assetId,
  fallback,
  triggerClassName,
  onTransitionSuccess,
}: Props) {
  const { t } = useTranslation('workflows');
  const { data: transitions, isLoading, error } = useValidTransitions(assetId);
  const [openTransition, setOpenTransition] = useState<ValidTransition | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Engine-driven path is unavailable: render the caller's legacy menu so
  // TOCA tenants see zero change.
  if (transitions === null) {
    return <>{fallback}</>;
  }

  if (isLoading) {
    return <Skeleton className="h-9 w-32" />;
  }

  // Unexpected network/error path: silently fall back. Better UX than
  // surfacing engine errors next to legacy UI.
  if (error) {
    return <>{fallback}</>;
  }

  // Engine returned an empty validNext list — this is a terminal state.
  // Render fallback so the user still has whatever the legacy page offers.
  if (!transitions || transitions.length === 0) {
    return <>{fallback}</>;
  }

  const open = (t: ValidTransition) => {
    setOpenTransition(t);
    setDialogOpen(true);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={triggerClassName}>
            <GitBranch className="h-4 w-4 mr-2" />
            {t('stageActions.title')}
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs uppercase tracking-wide">
            {t('stageActions.title')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {transitions.map((t) => (
            <DropdownMenuItem
              key={t.code}
              onClick={() => open(t)}
              disabled={!t.allowed}
              className="flex flex-col items-start gap-1 py-2"
            >
              <div className="flex items-center justify-between w-full">
                <span className="font-medium">{t.name}</span>
                {!t.allowed && (
                  <Badge variant="outline" className="text-[10px]">
                    {t.denyReasonCode ?? 'denied'}
                  </Badge>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground font-mono">
                → {t.toStateCode}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <TransitionDialog
        assetId={assetId}
        transition={openTransition}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={onTransitionSuccess}
      />
    </>
  );
}
